import { Banner, BlockStack, Box, Button, ButtonGroup, Icon, InlineStack, ProgressBar, Spinner, Text } from '@shopify/polaris';
import { CheckCircleIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePdfBlobCache } from '../../../shared/components/pdf/usePdfBlobCache';
import { DOC_TYPE_META } from '../../../shared/components/approvals/approvalsApi';
import { useApprovalQueue } from '../hooks/useApprovalQueue';
import { useCardCache } from '../hooks/useCardCache';
import { useWorkspaceHotkeys } from '../hooks/useWorkspaceHotkeys';

/*
  Deck controller shared by the approval queue and the intake queue: loads the light list, keeps the current card
  and the next one ready (card payload + first PDF), shows progress, animates decisions and wires the keyboard
  shortcuts to the actions the card registers.
*/
export default function QueueShell({
  docType,
  source = 'queue',
  lane = 'unprocessed',
  initialId = null,
  renderCard,
  emptyTitle = 'All done!',
  emptyText = 'Nothing is waiting for you.',
  onDecided,
  setToast,
  setErrorToast
}) {
  const meta = DOC_TYPE_META[docType];
  const deck = useApprovalQueue({ docType, source, lane, initialId });
  const cards = useCardCache(docType, deck.current?.id, deck.nextItem?.id);
  const pdfCache = usePdfBlobCache(meta.apiResource);
  const actionsRef = useRef({});
  const [animating, setAnimating] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const registerActions = useCallback((actions) => {
    actionsRef.current = actions || {};
  }, []);

  // prefetch the first PDF of the next card
  useEffect(() => {
    const key = (cards.nextCard?.s3Keys || []).find((k) => String(k).toLowerCase().endsWith('.pdf')) || cards.nextCard?.s3Keys?.[0];
    if (key) pdfCache.load(key);
  }, [cards.nextCard, pdfCache]);

  const decideWithAnimation = useCallback((kind, fn) => {
    setAnimating(kind);
    setTimeout(() => {
      setAnimating(null);
      fn();
    }, 260);
  }, []);

  useWorkspaceHotkeys({
    approve: () => actionsRef.current.approve && actionsRef.current.approve(),
    reject: () => actionsRef.current.reject && actionsRef.current.reject(),
    skip: () => deck.total > 1 && deck.skip(),
    next: deck.next,
    prev: deck.prev,
    help: () => setShowHelp((v) => !v),
    focusPdf: () => document.querySelector('iframe[title="PDF Viewer"]')?.focus()
  });

  if (deck.loading && !deck.items.length) {
    return (
      <Box padding="800">
        <InlineStack align="center">
          <Spinner />
        </InlineStack>
      </Box>
    );
  }
  if (deck.error) return <Banner tone="critical">{deck.error}</Banner>;

  if (!deck.current) {
    return (
      <Box padding="800">
        <BlockStack gap="400" inlineAlign="center">
          <div style={{ color: '#22AB38' }}>
            <Icon source={CheckCircleIcon} tone="success" />
          </div>
          <Text variant="headingLg" as="h2" alignment="center">
            {emptyTitle}
          </Text>
          <Text variant="bodyMd" tone="subdued" alignment="center">
            {emptyText}
          </Text>
          <Button onClick={() => deck.reload()}>Refresh</Button>
        </BlockStack>
      </Box>
    );
  }

  const total = Math.max(deck.initialTotal, deck.total + deck.decided);
  const progress = total > 0 ? Math.round((deck.decided / total) * 100) : 0;
  const card = cards.card;
  const wrapperStyle = {
    transition: 'transform 0.25s ease, opacity 0.25s ease',
    transform: animating === 'approve' ? 'translateX(60px)' : animating === 'reject' ? 'translateX(-60px)' : 'translateX(0)',
    opacity: animating ? 0 : 1
  };

  return (
    <BlockStack gap="300">
      <InlineStack align="space-between" blockAlign="center" wrap>
        <InlineStack gap="300" blockAlign="center">
          <Text variant="bodySm" tone="subdued">{`${Math.min(deck.index + 1, deck.total)} of ${deck.total} open · ${deck.decided} done`}</Text>
          <div style={{ width: 160 }}>
            <ProgressBar progress={progress} size="small" tone="primary" />
          </div>
        </InlineStack>
        <InlineStack gap="200" blockAlign="center">
          <Button size="slim" variant="plain" onClick={() => setShowHelp((v) => !v)}>
            Shortcuts (?)
          </Button>
          <ButtonGroup variant="segmented">
            <Button size="slim" onClick={deck.prev} disabled={deck.index === 0}>
              Previous
            </Button>
            <Button size="slim" onClick={deck.next} disabled={deck.index >= deck.total - 1}>
              Next
            </Button>
          </ButtonGroup>
        </InlineStack>
      </InlineStack>
      {showHelp ? (
        <Banner onDismiss={() => setShowHelp(false)}>
          <Text as="p">A approve · R reject · S skip · ← → previous / next · P focus the PDF. Shortcuts are off while you type in a field.</Text>
        </Banner>
      ) : null}
      <div style={wrapperStyle}>
        {card && !card.error ? (
          renderCard({
            card,
            setCard: cards.setCard,
            deck: {
              ...deck,
              removeCurrent: () =>
                decideWithAnimation('approve', () => {
                  cards.invalidate(deck.current.id);
                  deck.removeCurrent();
                })
            },
            registerActions,
            pdfCache,
            onDecided,
            setToast,
            setErrorToast,
            key: deck.current.id
          })
        ) : card?.error || cards.error ? (
          <Banner tone="critical">{card?.error || cards.error}</Banner>
        ) : (
          <Box padding="800">
            <InlineStack align="center">
              <Spinner />
            </InlineStack>
          </Box>
        )}
      </div>
    </BlockStack>
  );
}
